import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import "./Chatbot.css";
import { FaTimes, FaComments } from "react-icons/fa";
import { BsFillMicFill, BsFillMicMuteFill } from "react-icons/bs";

const suggestions = [
  "What services does TravXplo offer?",
  "How to partner my hotel?",
  "Show me luxury destinations",
  "Tell me about destination weddings",
  "Plan my honeymoon in Bali",
];
const predefinedResponses = {
  "what services does travxplo offer?": `TravXplo offers:\n• 🛎️ Luxury Travel Planning\n• 🧳 Corporate Travel Planning\n• 💍 Destination Wedding Support\n• 🌍 Custom AI Itineraries\n• 🏨 Hotel Collaboration Management\n• 📲 Influencer Concierge Model`,
  "how to partner my hotel?":
    "To partner your hotel with TravXplo, drop us a message at hello@travxplo.com or use our Contact Us section. We’ll get back within 24 hours!",
  "show me luxury destinations":
    "Some of our top luxury destinations include Maldives, Bora Bora, Amalfi Coast, Santorini, and Dubai.",
  "tell me about destination weddings":
    "We specialize in organizing unforgettable destination weddings — venue curation, local coordination, luxury experiences — all managed end-to-end.",
};

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      sender: "bot",
      text: "Hi! 👋 I’m TravXplo Assistant. You can ask me in English, Hindi, French, Spanish, or any language you prefer. How can I help you today?",
    },
  ]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [listening, setListening] = useState(false);
  const [voiceTriggered, setVoiceTriggered] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const startListening = () => {
    if (!("webkitSpeechRecognition" in window)) {
      alert("Sorry, your browser doesn't support speech recognition.");
      return;
    }

    const recognition = new window.webkitSpeechRecognition();
    recognition.lang = "en-US";
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    setListening(true);
    setVoiceTriggered(true);

    recognition.start();

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setInput(transcript);
      handleSend(transcript);
    };

    recognition.onerror = (event) => {
      console.error("Speech recognition error", event);
      alert("Error recognizing speech. Try again.");
    };

    recognition.onend = () => {
      setListening(false);
    };
  };

  const speak = (text) => {
    if ("speechSynthesis" in window) {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = "en";
      window.speechSynthesis.speak(utterance);
    }
  };

  const toggleChat = () => {
    setIsOpen(!isOpen);
  };

  const handleSend = async (customText) => {
    const text = customText || input.trim();
    if (text === "") return;

    const userMessage = { sender: "user", text };
    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setIsLoading(true);
    setShowSuggestions(false);

    const lower = text.toLowerCase();

    // ✅ Fixed responses
    if (predefinedResponses[lower]) {
      const botReply = predefinedResponses[lower];
      setMessages((prev) => [...prev, { sender: "bot", text: botReply }]);
      setIsLoading(false);
      if (voiceTriggered) speak(botReply);
      return;
    }
    // ✅ Corporate Travel Planning
    if (
      lower.includes("corporate travel") ||
      lower.includes("business trip") ||
      lower.includes("office travel") ||
      lower.includes("company travel")
    ) {
      const botReply = `We can help you organize your corporate travel seamlessly! ✈️<br/>
  Please <a href="/CorporateTravelFormModal" target="_blank" style="color:#007bff; font-weight:bold; text-decoration:none;">click here</a> to fill our Corporate Travel Planning form.`;

      setMessages((prev) => [
        ...prev,
        { sender: "bot", text: botReply, isLink: true },
      ]);
      setIsLoading(false);
      if (voiceTriggered)
        speak("Click the link to fill the corporate travel form");
      return;
    }

    // ✅ Destination Wedding Planning
    if (
      lower.includes("destination wedding") ||
      lower.includes("wedding") ||
      lower.includes("marriage") ||
      lower.includes("wedding plan")
    ) {
      const botReply = `We’d love to help you plan your dream destination wedding 💍<br/>
      Please   <a href="https://www.travxplo.com/DestinationWeddingFormModal" target="_blank" style="color:#007bff; font-weight:bold; text-decoration:none;">click here</a> to fill the Destination Wedding form.`;
      setMessages((prev) => [
        ...prev,
        { sender: "bot", text: botReply, isLink: true },
      ]);
      setIsLoading(false);
      if (voiceTriggered)
        speak("Click the link to fill the wedding planning form");
      return;
    }

    if (
      lower.includes("plan") ||
      lower.includes("itinerary") ||
      lower.includes("trip") ||
      lower.includes("honeymoon") ||
      lower.includes("book")
    ) {
      const botReply = `Click <a href="https://www.travxplo.com/AIItinerary" target="_blank" style="color:#007bff; font-weight:bold; text-decoration:none;">here</a> to plan your trip 🧳`;
      setMessages((prev) => [
        ...prev,
        { sender: "bot", text: botReply, isLink: true },
      ]);
      setIsLoading(false);
      if (voiceTriggered) speak("Click the link to plan your trip");
      return;
    }

    try {
      const response = await axios.post(
        "https://api.openai.com/v1/chat/completions",
        {
          model: "gpt-3.5-turbo",
          messages: [
            {
              role: "system",
              content:
                "You are a friendly travel assistant for TravXplo. Respond concisely in the user's language. Help with travel planning, luxury trips, weddings, hotels, or general queries.",
            },
            { role: "user", content: text },
          ],
          temperature: 0.7,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer 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`,
          },
        }
      );

      const aiReply = response.data.choices[0].message.content;
      setMessages((prev) => [...prev, { sender: "bot", text: aiReply }]);
      if (voiceTriggered) speak(aiReply);
    } catch (error) {
      console.error("AI Error:", error);
      setMessages((prev) => [
        ...prev,
        {
          sender: "bot",
          text: "Sorry, something went wrong. Please try again.",
        },
      ]);
    } finally {
      setIsLoading(false);
      setVoiceTriggered(false); // Reset flag
    }
  };

  return (
    <>
      {isOpen ? (
        <div className="chatbot-box">
          <div className="chat-header">
            <span>TravXplo Assistant</span>
            <FaTimes className="close-icon" onClick={toggleChat} />
          </div>

          <div className="chat-window">
            {messages.map((msg, i) => (
              // <div key={i} className={`chat-message ${msg.sender}`}>
              //   {msg.text.split("\n").map((line, index) => (
              //     <p key={index}>{line}</p>
              //   ))}
              // </div>
              <div key={i} className={`chat-message ${msg.sender}`}>
                {msg.isLink ? (
                  <p dangerouslySetInnerHTML={{ __html: msg.text }} />
                ) : (
                  msg.text
                    .split("\n")
                    .map((line, index) => <p key={index}>{line}</p>)
                )}
              </div>
            ))}
            {isLoading && <div className="chat-message bot">Typing...</div>}
            <div ref={messagesEndRef} />
          </div>

          {showSuggestions && (
            <div className="chat-suggestions">
              {suggestions.map((text, i) => (
                <button key={i} onClick={() => handleSend(text)}>
                  {text}
                </button>
              ))}
            </div>
          )}

          <div className="chat-input">
            <input
              type="text"
              placeholder={listening ? "Listening..." : "Type your message..."}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSend()}
              disabled={listening}
            />
            <button onClick={startListening} disabled={isLoading}>
              {listening ? <BsFillMicMuteFill /> : <BsFillMicFill />}
            </button>
            <button onClick={() => handleSend()} disabled={isLoading}>
              Send
            </button>
          </div>
        </div>
      ) : (
        <div className="chat-toggle-button" onClick={toggleChat}>
          <FaComments size={24} />
        </div>
      )}
    </>
  );
};

export default Chatbot;
